import React from 'react';

import { Col, Row } from '@opendoor/atoms/box';
import { Text } from '@opendoor/atoms/text';
import { cn } from '@opendoor/atoms/utils';
import { useNavigate } from 'react-router';

export interface SidebarNavItem {
  icon: React.ReactNode;
  isActive?: boolean;
  isDisabled?: boolean;
  link: string;
  name: string;
}

export interface SidebarNavProps {
  navBarItems: Array<SidebarNavItem>;
}

/**
 * SidebarNav component.
 * Renders horizontally on mobile (scrollable tabs) and vertically on desktop.
 */
export const SidebarNav: React.FC<SidebarNavProps> = ({ navBarItems }) => {
  const navigate = useNavigate();

  return (
    <Row className="gap-2 lg:flex-col lg:gap-4">
      {navBarItems.map((item) => {
        const key = `${item.link}-${item.name}`;

        const handlePress = () => {
          navigate(item.link);
        };

        if (item.isDisabled) {
          return (
            <Row
              key={key}
              className="cursor-not-allowed items-center gap-2 px-3 py-3 opacity-50 lg:px-0"
            >
              <Col className="hidden size-6 items-center justify-center lg:flex">{item.icon}</Col>
              <Text as="span" className="text-body-md truncate min-w-0">
                {item.name}
              </Text>
            </Row>
          );
        }

        return (
          <Row
            key={key}
            onClick={handlePress}
            aria-label={item.name}
            aria-current={item.isActive ? 'page' : undefined}
            analyticsName={`sidebar-nav-${item.name.toLowerCase()}`}
            className={cn(
              'cursor-pointer select-none items-center justify-center gap-2 rounded-sm px-3 py-3 hover:bg-od-bg-tertiary lg:px-0',
              item.isActive && 'bg-od-bg-tertiary font-bold',
            )}
          >
            <Col className="hidden size-6 items-center justify-center lg:flex">{item.icon}</Col>
            <Text as="span" className="text-body-md truncate min-w-0">
              {item.name}
            </Text>
          </Row>
        );
      })}
    </Row>
  );
};
